import React from "react";
import GlobalContext from "./GlobalContext";
import axios from "../Utils/axios";
import LocalStorage from "../Utils/localStorageService";
import LoadingComponent from "../components/Loading/LoadingComponent";
import { useNavigate } from "react-router-dom";

function AppContext(props) {
  const navigate = useNavigate();

  /* =========================================================
     STATES
  ========================================================= */

  const [isLoggedIn, setIsLoggedIn] = React.useState(
    !!LocalStorage.getAccessToken()
  );

  const [loading, setLoading] = React.useState(false);

  const [intervenantsData, setIntervenantsData] = React.useState([]);
  const [selectedIntervenantProfile, setSelectedIntervenantProfile] =
    React.useState(null);

  const [SelectedIntervenantId, setSelectedIntervenantId] =
    React.useState(null);

  const [SelectedSubsId, setSelectedSubsId] = React.useState(null);

  const [categories, setCategories] = React.useState([]);
  const [subs, setSubs] = React.useState([]);
  const [videoPacks, setVideoPacks] = React.useState([]);

  const [client, setClient] = React.useState(null);

  /* =========================================================
     FETCH GLOBAL DATA
  ========================================================= */

  React.useEffect(() => {
    const fetchGlobalData = async () => {
      try {
        setLoading(true);

        const [
          subsResponse,
          intervenantsResponse,
          videoPacksResponse,
          categoriesResponse,
        ] = await Promise.all([
          axios.get("/public/subs/g/get"),
          axios.get("/public/intervenant/list/g/profile"),
          axios.get("/public/video-packs/get"),
          axios.get("/public/g/cat/l/category"),
        ]);

        const intervenants =
          intervenantsResponse?.data?.result || [];

        const categoriesData =
          categoriesResponse?.data?.result || [];

        setSubs(subsResponse?.data?.result || []);
        setIntervenantsData(intervenants);
        setVideoPacks(videoPacksResponse?.data?.result || []);
        setCategories(categoriesData);

        /* CACHE */
        localStorage.setItem(
          "intervenantsData",
          JSON.stringify(intervenants)
        );

        localStorage.setItem(
          "categories",
          JSON.stringify(categoriesData)
        );
      } catch (error) {
        console.error("Erreur fetch global data :", error);
      } finally {
        setLoading(false);
      }
    };

    fetchGlobalData();
  }, []);

  /* =========================================================
     FETCH CLIENT PROFILE
  ========================================================= */

  React.useEffect(() => {
    if (!isLoggedIn) return;

    const fetchClient = async () => {
      try {
        setLoading(true);

        const response = await axios.get(
          "/client/g/get/p/profile"
        );

        setClient(response.data.result);
      } catch (error) {
        console.error("Erreur profil client :", error);

        LocalStorage.clearAccessToken();

        setIsLoggedIn(false);
        setClient(null);
      } finally {
        setLoading(false);
      }
    };

    fetchClient();
  }, [isLoggedIn]);

  /* =========================================================
     REFRESH INTERVENANT PROFILE
  ========================================================= */

  const refreshIntervenantProfile = async (id) => {
    try {
      setLoading(true);

      /*
        SOLUTION SAFE:
        on recharge toute la liste puis on filtre
        car ton endpoint /profile/:id retourne 404
      */

      const response = await axios.get(
        "/public/intervenant/list/g/profile"
      );

      const allIntervenants =
        response?.data?.result || [];

      setIntervenantsData(allIntervenants);

      const profile = allIntervenants.filter(
        (item) => item.id_user === id
      );

      if (profile.length > 0) {
        setSelectedIntervenantProfile(profile);

        localStorage.setItem(
          "selectedIntervenantProfile",
          JSON.stringify(profile)
        );
      }

      return profile;
    } catch (error) {
      console.error(
        "Erreur refresh intervenant profile :",
        error
      );
    } finally {
      setLoading(false);
    }
  };

  /* =========================================================
     AUTO REFRESH PROFILE AFTER PAGE RELOAD
  ========================================================= */

  React.useEffect(() => {
    const savedProfile = JSON.parse(
      localStorage.getItem("selectedIntervenantProfile")
    );

    if (savedProfile?.[0]?.id_user) {
      refreshIntervenantProfile(savedProfile[0].id_user);
    }
  }, []);

  /* =========================================================
     WHEN USER SELECT INTERVENANT
  ========================================================= */

  React.useEffect(() => {
    if (!SelectedIntervenantId) return;

    refreshIntervenantProfile(SelectedIntervenantId);
  }, [SelectedIntervenantId]);

  /* =========================================================
     LOGIN / LOGOUT
  ========================================================= */

  const login = () => {
    setIsLoggedIn(true);
  };

  const logout = async () => {
    try {
      setLoading(true);

      await axios.post(
        "/public/c/client/d/disconnect"
      );

      LocalStorage.clearAccessToken();

      localStorage.removeItem(
        "selectedIntervenantProfile"
      );

      setIsLoggedIn(false);

      setClient(null);

      setSelectedIntervenantProfile(null);

      navigate("/login");
    } catch (error) {
      console.error("Erreur logout :", error);
    } finally {
      setLoading(false);
    }
  };

  /* =========================================================
     CONTEXT VALUE
  ========================================================= */

  const contextValue = React.useMemo(
    () => ({
      isLoggedIn,
      loading,

      client,

      login,
      logout,

      intervenantsData,
      setIntervenantsData,

      selectedIntervenantProfile,
      setSelectedIntervenantProfile,

      refreshIntervenantProfile,

      SelectedIntervenantId,
      setSelectedIntervenantId,

      SelectedSubsId,
      setSelectedSubsId,

      categories,
      subs,
      videoPacks,
    }),
    [
      isLoggedIn,
      loading,
      client,
      intervenantsData,
      selectedIntervenantProfile,
      SelectedIntervenantId,
      SelectedSubsId,
      categories,
      subs,
      videoPacks,
    ]
  );

  /* =========================================================
     RENDER
  ========================================================= */

  return (
    <GlobalContext.Provider value={contextValue}>
      {loading ? (
        <LoadingComponent loading />
      ) : (
        props.children
      )}
    </GlobalContext.Provider>
  );
}

export default React.memo(AppContext);